import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  CreditCard,
  People,
  Lock,
  ExitToApp,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors, routes, token_keys } from '../constants';
import { performLogout } from '../utils/authUtils';
import LanguageSelector from "./LanguageSelector";
import SessionStatusModal from "./SessionStatusModal";
import useStore from "../store";

const drawerWidth = 240;

export default function PageHeader({ user, locale, setLocale, toggleSidebar, title, sidebarOpen = true }) {
  const navigate = useNavigate();
  const activeUser = useStore((state) => state.activeUser);
  const setActiveUser = useStore((state) => state.setActiveUser);
  const setAuthenticated = useStore((state) => state.setAuthenticated);
  const authenticated = useStore((state) => state.authenticated);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 900px)');

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNavigation = (route) => {
    navigate(route);
    handleMenuClose();
  };

  // localOnly: true keeps the Microsoft session alive ("stay signed in to
  // Microsoft"); the default is a full federated logout.
  const logout = async (localOnly = false) => {
    setActiveUser(null);
    setAuthenticated(false);
    handleMenuClose();
    const redirectUrl = await performLogout({ localOnly });
    if (redirectUrl) {
      // Entra full logout: hand off to Microsoft to end its session too.
      window.location.assign(redirectUrl);
      return;
    }
    navigate(routes.login);
  };

  // Session extension handler
  const handleExtendSession = async () => {
    try {
      // Import the refresh function here to avoid circular dependencies
      const { refreshAccessToken } = await import("../api/axiosInstance");
      const response = await refreshAccessToken();
      if (response.status === 200) {
        const expiresIn = response.data.expires_in || 900;
        const issuedAt = Date.now();
        localStorage.setItem(token_keys.expiration, expiresIn.toString());
        localStorage.setItem(token_keys.issued_at, issuedAt.toString());
        localStorage.setItem(token_keys.access_token, response.data.access_token);
        localStorage.setItem(token_keys.refresh_token, response.data.refresh_token);
        console.log("Session extended successfully from header");
      }
    } catch (error) {
      console.error("Failed to extend session from header:", error);
      // If extension fails, logout user
      logout();
    }
  };

  const displayName = activeUser?.first_name ? activeUser.first_name[0]?.toUpperCase() : "U";
  const displayEmail = activeUser?.email || "User";

  return (
    <AppBar
      position="fixed"
      sx={{
        background: 'linear-gradient(135deg, rgba(245, 247, 255, 0.95) 0%, rgba(203, 223, 241, 0.9) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 67, 142, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
        p: 1,
        width: isMobile ? "100%" : sidebarOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
        marginLeft: isMobile ? 0 : sidebarOpen ? `${drawerWidth}px` : 0,
        transition: "all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)",
        // position: 'relative',
        // '&::before': {
        //   content: '""',
        //   position: 'absolute',
        //   top: 0,
        //   left: 0,
        //   right: 0,
        //   bottom: 0,
        //   background: 'radial-gradient(circle at 20% 50%, rgba(0, 67, 142, 0.05) 0%, transparent 50%)',
        //   pointerEvents: 'none'
        // },
        // '&::after': {
        //   content: '""',
        //   position: 'absolute',
        //   top: 0,
        //   left: 0,
        //   right: 0,
        //   bottom: 0,
        //   background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
        //   pointerEvents: 'none'
        // }
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}>
        {/* Left Section: Menu Button + Page Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={toggleSidebar}
            sx={{
              color: "#00438E",
              backgroundColor: 'rgba(0, 67, 142, 0.1)',
              borderRadius: '12px',
              width: 44,
              height: 44,
              transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 67, 142, 0.15)',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 67, 142, 0.2)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              }
            }}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                left: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #00438E 0%, #1976d2 100%)',
                borderRadius: '2px',
                width: '100%',
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '.MuiTypography-root:hover + &': {
                  transform: 'scaleX(1)',
                }
              }}
            />
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Session Status - Commented Out for Space */}

          {/* Language Selector - Always Visible */}
          <LanguageSelector locale={locale} setLocale={setLocale} />

          {/* User Profile Dropdown */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              px: 1.5,
              py: 1,
              borderRadius: '16px',
              backgroundColor: 'rgba(0, 67, 142, 0.08)',
              border: '2px solid rgba(0, 67, 142, 0.15)',
              transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
              position: 'relative',
              '&:hover': {
                backgroundColor: 'rgba(0, 67, 142, 0.12)',
                border: '2px solid rgba(0, 67, 142, 0.25)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(0, 67, 142, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              }
            }}
            onClick={handleMenuOpen}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'linear-gradient(135deg, #00438E 0%, #1976d2 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.9rem',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 67, 142, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 16px rgba(0, 67, 142, 0.25)',
                }
              }}
            >
              {displayName}
            </Avatar>
            {!isMobile && (
              <Box sx={{ ml: 1.5, flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#00438E",
                    fontSize: '0.7rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {activeUser?.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0, 67, 142, 0.7)",
                    fontSize: '0.6rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayEmail}
                </Typography>
              </Box>
            )}
            <ExpandMoreIcon
              sx={{
                ml: 1,
                fontSize: 20,
                color: "#00438E",
                transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </Box>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: '16px',
                boxShadow: '0 12px 40px rgba(0, 67, 142, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
                minWidth: 250,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                overflow: 'hidden'
              }
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => handleNavigation(routes.account + "/profile")}
              sx={{
                py: 1.8,
                fontSize: "14px",
                color: "#00438E",
                fontWeight: 500,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: "rgba(0, 67, 142, 0.08)",
                  transform: 'translateX(4px)',
                },
                borderLeft: '3px solid transparent',
                '&:hover': {
                  borderLeft: '3px solid #00438E',
                }
              }}
            >
              <SettingsIcon sx={{ mr: 1.5, fontSize: 20, color: "#00438E" }} />
              Account Settings
            </MenuItem>
            {activeUser?.is_staff && (
            <MenuItem
              onClick={() => handleNavigation(routes.account + "/manage-users")}
              sx={{
                py: 1.8,
                fontSize: "14px",
                color: "#00438E",
                fontWeight: 500,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: "rgba(0, 67, 142, 0.08)",
                  transform: 'translateX(4px)',
                },
                borderLeft: '3px solid transparent',
                '&:hover': {
                  borderLeft: '3px solid #00438E',
                }
              }}
            >
              <People sx={{ mr: 1.5, fontSize: 20, color: "#00438E" }} />
              Manage Users
            </MenuItem>
            )}

            <Divider sx={{
              my: 1,
              borderColor: "rgba(0, 67, 142, 0.1)",
              borderWidth: '1px'
            }} />

            <MenuItem
              onClick={logout}
              sx={{
                py: 1.8,
                fontSize: "14px",
                color: "#d32f2f",
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                  transform: 'translateX(4px)',
                },
                borderLeft: '3px solid transparent',
                '&:hover': {
                  borderLeft: '3px solid #f44336',
                }
              }}
            >
              <ExitToApp sx={{ mr: 1.5, color: "#f44336", fontSize: 20 }} />
              Log Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Session Status Modal - Commented Out */}
    </AppBar>
  );
}
